import React, { useState, useMemo, useEffect, useRef } from "react";
import { FaTimes, FaPalette, FaSearch, FaInfoCircle, FaCloudUploadAlt, FaTrash } from "react-icons/fa";

const styleImagesContext = require.context("../../assets/style-thumbnails", false, /\.(gif|webp|png|jpe?g)$/);
const STYLE_IMAGE_BY_FILENAME = styleImagesContext.keys().reduce((acc, key) => {
  const cleanKey = key.replace("./", "");
  acc[cleanKey] = styleImagesContext(key);
  return acc;
}, {});

const STYLE_GROUPS = [
  {
    title: "E-Commerce",
    items: [
      { 
        id: "Catalog", 
        desc: "Clean product-focused vs. mood-driven collection-focused visuals", 
        image: "catalog.webp",
        tags: ["clean backdrop", "high clarity", "studio lighting", "commercial ready"],
        best_for_outfit_types: ["All Apparel", "Smart Casual", "Basic Wear"]
      },
      { 
        id: "Beauty Profile", 
        desc: "Close-up portraits highlighting makeup, accessories, or hair", 
        image: "beauty_profile.webp",
        tags: ["close-up", "beauty lighting", "minimal background"],
        best_for_outfit_types: ["Accessories", "Jewelry", "Makeup Looks"]
      },
      { 
        id: "Indoor Studio-1", 
        desc: "Model-centric, polished fashion portrait photography", 
        image: "indoor_studio1.webp",
        tags: ["studio setup", "neutral tones", "controlled lighting"],
        best_for_outfit_types: ["Formal Wear", "Luxury Wear", "High Fashion Editorial"]
      },
      // { 
      //   id: "Still Life", 
      //   desc: "Artistic product shots of garments or accessories", 
      //   image: "still_life.webp",
      //   tags: ["product focus", "props", "flatlay", "creative composition"],
      //   best_for_outfit_types: ["Accessories", "Footwear", "Bags", "Jewelry"]
      // },
      { 
        id: "Sport", 
        desc: "Dynamic performance looks with motion and energy", 
        image: "sports.webp",
        tags: ["movement", "action shot", "fitness vibe"],
        best_for_outfit_types: ["Athleisure", "Activewear", "Performance Gear"]
      }
    ]
  },
  {
    title: "Location",
    items: [
      { 
        id: "Street Style", 
        desc: "Candid, real-life personal fashion in urban settings", 
        image: "street_style.webp",
        tags: ["urban", "realistic", "everyday vibe", "casual chic"],
        best_for_outfit_types: ["Streetwear", "Smart Casual", "Urban Fashion"]
      },
      { 
        id: "Nature-1", 
        desc: "Pastoral, romantic, nature-forward lifestyle aesthetic", 
        image: "nature1.webp",
        tags: ["greenery", "sunlight", "earthy tones", "natural light"],
        best_for_outfit_types: ["Boho", "Ethnic Casual", "Outdoor Wear"]
      },
      { 
        id: "Urban Garden", 
        desc: "Conceptual, brand-driven fashion imagery for campaigns", 
        image: "urban_garden.webp",
        tags: ["greenery meets architecture", "modern luxury", "editorial mood"],
        best_for_outfit_types: ["Luxury Wear", "Sustainable Fashion", "Modern Chic"]
      },
      { 
        id: "Urban Architecture", 
        desc: "Model in real-life settings like street, café, beach; mood-driven", 
        image: "urban_architecture.webp",
        tags: ["city backdrop", "modern lifestyle", "editorial"],
        best_for_outfit_types: ["Streetwear", "Smart Casual", "Chic Urban Fashion"]
      },
      { 
        id: "Luxury Staircase", 
        desc: "Luxurious, dramatic, avant-garde couture presentations", 
        image: "luxury_staircase.webp",
        tags: ["grand interiors", "elegant tone", "dramatic lines"],
        best_for_outfit_types: ["Luxury Couture", "Evening Gowns", "Formal Wear"]
      },
      { 
        id: "Beach", 
        desc: "Sunlit coastal setting ideal for summer and swimwear campaigns", 
        image: "beach.webp",
        tags: ["sunny", "outdoors", "vacation vibe", "coastal light"],
        best_for_outfit_types: ["Swimwear", "Resort Wear", "Beach Casual"]
      },
      { 
        id: "Mountain", 
        desc: "Scenic elevated terrain with rugged textures and wind", 
        image: "mountain.webp",
        tags: ["natural terrain", "outdoor adventure", "windy mood"],
        best_for_outfit_types: ["Winter Outerwear", "Athleisure", "Outdoor Gear"]
      },
      { 
        id: "Canyons", 
        desc: "Earth-toned rock formations and vast landscapes", 
        image: "canyons.webp",
        tags: ["earthy tones", "natural contrast", "sunlight depth"],
        best_for_outfit_types: ["Boho", "Festival Wear", "Desert Collection"]
      },
      { 
        id: "Desert", 
        desc: "Golden sand dunes with strong sunlight and bohemian vibe", 
        image: "desert.webp",
        tags: ["warm tones", "boho energy", "sunlight shadows"],
        best_for_outfit_types: ["Boho", "Festival Wear", "Ethnic Fusion"]
      },
      { 
        id: "Snow", 
        desc: "Bright snow landscape ideal for winter apparel", 
        image: "snow.webp",
        tags: ["white backdrop", "cold light", "crisp contrast"],
        best_for_outfit_types: ["Winter Outerwear", "Knitted Wear", "Coats"]
      },
      { 
        id: "Forest", 
        desc: "Tree-covered nature with mystery and depth of field", 
        image: "forest.webp",
        tags: ["dense greenery", "soft shade", "natural mystery"],
        best_for_outfit_types: ["Outdoor Fashion", "Earth Tones", "Bohemian"]
      },
      { 
        id: "Meadow", 
        desc: "Soft grassy field with pastel daylight and calm mood", 
        image: "meadow.webp",
        tags: ["pastel light", "open field", "romantic"],
        best_for_outfit_types: ["Floral Dresses", "Picnic Wear", "Vintage"]
      },
      { 
        id: "Poolside", 
        desc: "Reflective water backdrop with luxury vibes", 
        image: "poolside.webp",
        tags: ["resort", "luxury lifestyle", "tropical light"],
        best_for_outfit_types: ["Swimwear", "Resort Wear", "Luxury Casual"]
      },
      { 
        id: "Rooftop", 
        desc: "High view cityscape with evening light", 
        image: "rooftop.webp",
        tags: ["city night", "evening glow", "luxury vibe"],
        best_for_outfit_types: ["Party Wear", "Urban Chic", "Evening Fashion"]
      },
      { 
        id: "Graffiti Wall", 
        desc: "Bold urban background with street culture vibe", 
        image: "graffiti_wall.webp",
        tags: ["color pop", "youthful", "edgy streetwear"],
        best_for_outfit_types: ["Streetwear", "Denim", "Youth Fashion"]
      },
      { 
        id: "Industrial Warehouse", 
        desc: "Raw metal and shadow-heavy structure", 
        image: "warehouse.webp",
        tags: ["grunge", "raw texture", "editorial fashion"],
        best_for_outfit_types: ["High Fashion Editorial", "Denim", "Monochrome Looks"]
      },
      { 
        id: "European Street", 
        desc: "Classic European alley with old stone aesthetic", 
        image: "european_street.webp",
        tags: ["romantic", "vintage tones", "travel aesthetic"],
        best_for_outfit_types: ["Dresses", "Smart Casual", "Travel Wear"]
      }
    ]
  },
  {
    title: "Concept",
    items: [
      { 
        id: "Coverpage", 
        desc: "Narrative-rich, magazine-style fashion storytelling", 
        image: "coverpage.webp",
        tags: ["editorial", "storytelling", "luxury visual"],
        best_for_outfit_types: ["High Fashion", "Runway Looks", "Statement Pieces"]
      },
      { 
        id: "Indoor Studio-2", 
        desc: "Artistically daring, boundary-pushing fashion imagery", 
        image: "indoor_studio2.webp",
        tags: ["creative lighting", "artistic direction", "editorial concept"],
        best_for_outfit_types: ["Couture", "Experimental Fashion", "Designer Wear"]
      },
      { 
        id: "Rampwalk", 
        desc: "Action-focused captures of fashion in motion on the catwalk", 
        image: "rampwalk.webp",
        tags: ["runway", "high energy", "movement capture"],
        best_for_outfit_types: ["High Fashion", "Luxury Couture", "Event Wear"]
      }
    ]
  },
  {
    title: "Lifestyle",
    items: [
      { id: "Bedroom", desc: "Intimate indoor setting with soft light", image: "bedroom.webp", tags: ["intimate", "soft lighting", "comfort vibe"], best_for_outfit_types: ["Loungewear", "Intimates", "Sleepwear"] },
      { id: "Bathroom", desc: "Clean tile or marble backdrop with fresh aesthetic", image: "bathroom.webp", tags: ["clean", "minimal", "skincare aesthetic"], best_for_outfit_types: ["Robes", "Beauty Wear", "Loungewear"] },
      { id: "Kitchen", desc: "Warm home setting with lifestyle storytelling", image: "kitchen.webp", tags: ["homey", "casual", "natural"], best_for_outfit_types: ["Casual Wear", "Loungewear", "Daywear"] },
      { id: "Café", desc: "Urban coffee shop aesthetic with cozy textures", image: "cafe.webp", tags: ["urban", "cozy", "social lifestyle"], best_for_outfit_types: ["Streetwear", "Smart Casual", "Urban Fashion"] },
      { id: "Bookstore", desc: "Warm indoor space with books and wooden accents", image: "bookstore.webp", tags: ["soft academia", "vintage vibe", "indoor mood"], best_for_outfit_types: ["Layered Fashion", "Vintage", "Smart Casual"] },
      { id: "Balcony", desc: "Cozy terrace or balcony with natural light", image: "balcony.webp", tags: ["sunlight", "cozy", "relaxed"], best_for_outfit_types: ["Loungewear", "Daywear", "Smart Casual"] },
      { id: "Airport", desc: "Travel lifestyle vibe with luggage and movement", image: "airport.webp", tags: ["travel", "movement", "transit vibe"], best_for_outfit_types: ["Smart Casual", "Streetwear", "Travel Sets"] },
      { id: "Workspace", desc: "Home office or work setup", image: "workspace.webp", tags: ["professional", "minimal", "modern lifestyle"], best_for_outfit_types: ["Formal Wear", "Smart Casual", "Workwear"] }
    ]
  },
  {
    title: "Festive",
    items: [
      { id: "Diwali Courtyard", desc: "Festive Indian courtyard with warm decorative lights", image: "diwali_courtyard.webp", tags: ["festive", "warm lights", "ethnic vibe"], best_for_outfit_types: ["Ethnic Occasion", "Festive Wear", "Traditional Outfits"] },
      { id: "Christmas Cabin", desc: "Cozy indoor winter setting with fairy lights", image: "christmas_cabin.webp", tags: ["winter festive", "cozy", "holiday feel"], best_for_outfit_types: ["Winterwear", "Knitwear", "Festive Casual"] },
      { id: "Lantern Street", desc: "Cultural night market scene with lanterns and crowds", image: "lantern_street.webp", tags: ["night festival", "vibrant", "cultural"], best_for_outfit_types: ["Fusion Wear", "Street Casual", "Festive Collection"] },
      { id: "Moroccan Riad", desc: "Exotic courtyard with patterns and warm tones", image: "moroccan_riad.webp", tags: ["travel culture", "patterned walls", "heritage vibe"], best_for_outfit_types: ["Ethnic Fusion", "Vacation Wear", "Luxury Resort"] },
      { id: "Santorini Seaside", desc: "White and blue Mediterranean seaside setting", image: "santorini_seaside.webp", tags: ["coastal chic", "bright light", "luxury vacation"], best_for_outfit_types: ["Resort Wear", "Beach Casual", "Summer Fashion"] }
    ]
  },
  {
    title: "Editorial",
    items: [
      { id: "Theatre Stage", desc: "Retro stage lighting for dramatic poses", image: "theatre_stage.webp", tags: ["dramatic lighting", "retro glam", "cinematic"], best_for_outfit_types: ["Evening Gowns", "Luxury Couture", "High Fashion"] },
      { id: "Art Gallery", desc: "Minimal gallery space with abstract art background", image: "art_gallery.webp", tags: ["modern", "editorial", "clean lines"], best_for_outfit_types: ["Minimal Fashion", "Luxury Wear", "Contemporary Style"] },
      { id: "Rainy Street", desc: "Wet reflective pavement and neon reflections", image: "rainy_street.webp", tags: ["night aesthetic", "moody", "cinematic city vibe"], best_for_outfit_types: ["Streetwear", "Trench Coats", "Urban Fashion"] },
      { id: "Neon Alley", desc: "Neon-lit alleyway with reflections and signage", image: "neon_alley.webp", tags: ["cyberpunk", "urban night", "vibrant color"], best_for_outfit_types: ["Streetwear", "Techwear", "Nightwear Fashion"] },
      { id: "Warehouse Loft", desc: "Open industrial loft with concrete walls", image: "warehouse_loft.webp", tags: ["grunge", "industrial light", "editorial setup"], best_for_outfit_types: ["High Fashion", "Denim", "Monochrome"] }
    ]
  },
  {
    title: "Futuristic",
    items: [
      { id: "Vaporwave Gradient", desc: "Soft retro pastel background inspired by vaporwave art", image: "vaporwave_gradient.webp", tags: ["retro", "digital pastel", "pop culture"], best_for_outfit_types: ["Streetwear", "Youth Fashion", "Experimental Looks"] },
      { id: "Cyberpunk City", desc: "Neon-lit futuristic urban skyline with reflections", image: "cyberpunk_city.webp", tags: ["futuristic", "neon", "digital fashion"], best_for_outfit_types: ["Techwear", "Futuristic Couture", "High Fashion Editorial"] },
      { id: "AI Glitch World", desc: "Abstract glitch art inspired backdrop", image: "ai_glitch_world.webp", tags: ["digital art", "conceptual", "surreal"], best_for_outfit_types: ["Experimental Fashion", "Avant Garde", "Runway Concepts"] },
      { id: "Retro Diner", desc: "80s inspired neon diner aesthetic", image: "retro_diner.webp", tags: ["nostalgic", "pop", "youthful"], best_for_outfit_types: ["Streetwear", "Retro Fashion", "Denim Casual"] },
      { id: "Roller Rink Neon", desc: "Playful retro roller-skating rink with colored lights", image: "roller_rink.webp", tags: ["playful", "retro", "fun energy"], best_for_outfit_types: ["Streetwear", "Youth Casual", "Festival Looks"] }
    ]
  }
];

// Get first 6 styles from all groups, with selected style first if provided
const getFirstSixStyles = (selectedStyle = null) => {
  const allStyles = STYLE_GROUPS.flatMap(group => 
    group.items.map(item => ({ ...item, category: group.title }))
  );
  
  // If there's a selected style, move it to the front
  if (selectedStyle) {
    const selectedItem = allStyles.find(item => item.id === selectedStyle);
    if (selectedItem) {
      const otherItems = allStyles.filter(item => item.id !== selectedStyle);
      return [selectedItem, ...otherItems.slice(0, 5)];
    }
  }
  
  return allStyles.slice(0, 6);
};

const StyleGrid = ({ 
  options = null, 
  selectedStyle, 
  onSelect, 
  variant = 'inline', // 'inline' or 'modal'
  showTitle = true,
  className = ""
}) => {
  // Use provided options or get first 6 styles with selected style prioritized
  const displayOptions = options || getFirstSixStyles(selectedStyle);
  const isModal = variant === 'modal';
  
  const gridClasses = isModal 
    ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
    : "grid grid-cols-3 gap-2 sm:gap-3";
  
  const containerClasses = isModal
    ? "glass-effect rounded-3xl border border-white/20 bg-[#0a0a0a]/95 backdrop-blur-3xl p-6 sm:p-8 w-[90vw] max-w-6xl max-h-[85vh] overflow-y-auto shadow-2xl flex flex-col"
    : `w-full ${className}`;

  return (
    <div className={containerClasses}>
      {showTitle && (
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-2 rounded-xl bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-300/20">
              <FaPalette className="text-pink-300 text-lg" />
            </div>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-2">Choose Photo Style</h2>
          <p className="text-gray-400 text-sm sm:text-base">Select a style most suited for your amazing product</p>
        </div>
      )}

      <div className={gridClasses}>
        {displayOptions.map(style => (
          <button
            key={style.id}
            type="button"
            onClick={() => onSelect(style.id)}
            className={`group relative overflow-hidden rounded-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl border border-white/10 hover:border-white/20 ${
              selectedStyle === style.id
                ? 'ring-1 ring-pink-400 ring-offset-1 ring-offset-[#0a0a0a] shadow-lg shadow-pink-400/25 border-pink-400/50'
                : 'hover:ring-1 hover:ring-white/30'
            }`}
            title={style.id}
          >
            <div className="aspect-square relative">
              {style.image && STYLE_IMAGE_BY_FILENAME[style.image] ? (
                <img 
                  src={STYLE_IMAGE_BY_FILENAME[style.image]} 
                  alt={style.id} 
                  className="w-full h-full object-cover" 
                  loading="lazy" 
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-white/5 to-white/0" />
              )}

              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

              <div className="absolute bottom-1 left-1 right-1 text-center">
                <div className="text-white font-semibold text-[10px] sm:text-xs leading-tight drop-shadow-lg">
                  {style.id}
                </div>
              </div>

              {selectedStyle === style.id && (
                <div className="absolute top-1 right-1 w-6 h-6 bg-pink-400 rounded-full flex items-center justify-center">
                  <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                </div>
              )}

              <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-200" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const StyleCard = ({ item, isSelected, onClick }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <button
      type="button"
      className={`group relative overflow-hidden rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl ${
        isSelected 
          ? 'ring-2 ring-pink-400 ring-offset-2 ring-offset-[#0a0a0a] shadow-lg shadow-pink-400/25' 
          : 'hover:ring-1 hover:ring-white/30'
      }`}
      onClick={onClick}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div className="aspect-square relative">
        <img
          src={STYLE_IMAGE_BY_FILENAME[item.image]}
          alt={item.id}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Style ID - Bold text in center-bottom */}
        <div className="absolute bottom-0.5 sm:bottom-1 left-0.5 sm:left-1 right-0.5 sm:right-1 text-center">
          <div className="text-white font-bold text-[7px] sm:text-[10px] lg:text-[9px] leading-tight drop-shadow-lg">
            {item.id}
          </div>
        </div>
        
        {/* Info icon for tooltip - hidden on mobile */}
        <div className="hidden sm:block absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <FaInfoCircle className="text-white/80 text-[10px] lg:text-[8px]" />
        </div>
        
        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute top-0.5 sm:top-1 right-0.5 sm:right-1 w-3 h-3 sm:w-4 sm:h-4 lg:w-3.5 lg:h-3.5 bg-pink-400 rounded-full flex items-center justify-center shadow-lg">
            <svg className="w-1.5 h-1.5 sm:w-2 sm:h-2 lg:w-1.5 lg:h-1.5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        )}
        
        {/* Hover effect */}
        <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-200" />
        
        {/* Tooltip on hover - hidden on mobile */}
        {showTooltip && (
          <div className="hidden sm:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-black/95 text-white text-xs rounded-lg whitespace-normal w-48 text-center z-10 pointer-events-none">
            <div className="font-semibold mb-1">{item.id}</div>
            <div className="text-white/80">{item.desc}</div>
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
              <div className="border-4 border-transparent border-t-black/95" />
            </div>
          </div>
        )}
      </div>
    </button>
  );
};

const StyleModal = ({
  isOpen,
  onClose,
  selectedStyle,
  setSelectedStyle,
  isPreview = false,
  className = ""
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  // Get all categories
  const categories = ["All", ...STYLE_GROUPS.map(g => g.title)];

  // Initialize state from selectedStyle prop
  useEffect(() => {
    if (isOpen && !isPreview) {
      // Check if selectedStyle is an object with image upload
      if (selectedStyle && typeof selectedStyle === 'object' && selectedStyle.isImageUpload && selectedStyle.uploadedImageFile) {
        setUploadedImage(selectedStyle.uploadedImageFile);
        setImagePreview(prevPreview => {
          if (prevPreview && prevPreview.startsWith('blob:')) {
            URL.revokeObjectURL(prevPreview);
          }
          // Always create a new blob URL from the file object to ensure it's valid
          return URL.createObjectURL(selectedStyle.uploadedImageFile);
        });
      } else {
        setUploadedImage(null);
        setImagePreview(prevPreview => {
          if (prevPreview && prevPreview.startsWith('blob:')) {
            URL.revokeObjectURL(prevPreview);
          }
          return null;
        });
      }
    }
  }, [isOpen, isPreview, selectedStyle]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  // Filter styles based on search and category - flatten all items
  const filteredItems = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    
    // Flatten all items from all groups
    const allItems = STYLE_GROUPS.flatMap(group => 
      group.items.map(item => ({ ...item, category: group.title }))
    );
    
    // Filter based on search and category
    const filtered = allItems.filter(item => {
      const matchesSearch = !query || 
        item.id.toLowerCase().includes(query) || 
        item.desc.toLowerCase().includes(query) ||
        (item.tags && item.tags.some(tag => tag.toLowerCase().includes(query))) ||
        (item.best_for_outfit_types && item.best_for_outfit_types.some(type => type.toLowerCase().includes(query)));
      const matchesCategory = selectedCategory === "All" || item.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });

    // If there's a selected style (string ID), move it to the front
    const styleId = typeof selectedStyle === 'string' ? selectedStyle : (selectedStyle?.styleId || null);
    if (styleId) {
      const selectedItem = filtered.find(item => item.id === styleId);
      if (selectedItem) {
        const otherItems = filtered.filter(item => item.id !== styleId);
        return [selectedItem, ...otherItems];
      }
    }

    return filtered;
  }, [searchQuery, selectedCategory, selectedStyle]);

  // Count total filtered styles
  const totalStyles = filteredItems.length;
  const totalAllStyles = STYLE_GROUPS.reduce((sum, group) => sum + group.items.length, 0);

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("All");
  };

  // Handle image upload with validation and in-memory cloning to avoid file handle churn
  const handleImageUpload = async (e) => {
    if (isPreview) return;
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Validate file type (JPG/PNG only)
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validImageTypes.includes(file.type)) {
      alert('Please upload only JPG or PNG image files.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert('Image size should be less than 10MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Clone file into memory so later uploads don't depend on original handle
    let inMemoryFile = file;
    try {
      const buffer = await file.arrayBuffer();
      inMemoryFile = new File([buffer], file.name, { type: file.type, lastModified: file.lastModified || Date.now() });
    } catch (cloneErr) {
      console.warn('Could not clone style image file, using original reference', cloneErr);
    }

    const preview = URL.createObjectURL(inMemoryFile);
    
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }

    setUploadedImage(inMemoryFile);
    setImagePreview(preview);
    
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Remove uploaded image
  const handleRemoveImage = () => {
    if (isPreview) return;
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setUploadedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Handle style selection - clear image if style is selected
  const handleStyleSelect = (styleId) => {
    if (isPreview) return;
    // Clear image when style is selected
    if (uploadedImage) {
      handleRemoveImage();
    }
    setSelectedStyle(styleId);
    if (!isPreview) onClose();
  };

  // Handle apply button (for image upload)
  const handleApply = () => {
    if (uploadedImage) {
      setSelectedStyle({
        uploadedImageFile: uploadedImage,
        imagePreview: imagePreview,
        isImageUpload: true
      });
    }
    if (typeof onClose === 'function') onClose();
  };

  // Handle reset
  const handleReset = () => {
    if (isPreview) return;
    if (uploadedImage) {
      handleRemoveImage();
    }
    setSelectedStyle(null);
  };

  if (!isOpen && !isPreview) return null;

  const Panel = (
    <div className={`glass-effect rounded-3xl border border-white/20 bg-[#0a0a0a]/95 backdrop-blur-3xl p-3 sm:p-6 lg:p-8 w-[95vw] max-w-4xl max-h-[92vh] overflow-hidden flex flex-col ${isPreview ? '' : 'shadow-2xl'}`}>
      {/* Header */}
      <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-4 lg:mb-5 flex-shrink-0">
        <div className="p-1.5 sm:p-2 rounded-lg sm:rounded-xl bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-300/20">
          <FaPalette className="text-pink-300 text-sm sm:text-base lg:text-lg" />
        </div>
        <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-white tracking-tight">Choose Your Style</h3>
        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <span className="text-xs sm:text-sm text-gray-400">
            {totalStyles} {totalStyles === 1 ? 'style' : 'styles'}
          </span>
          {!isPreview && (
            <button 
              type="button" 
              className="p-1.5 sm:p-2 rounded-lg sm:rounded-xl text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-200" 
              onClick={onClose} 
              title="Close"
            >
              <FaTimes className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Scrollable Content Area - Contains Upload, OR Separator, Search/Filters, and Style Cards */}
      <div className="flex-1 overflow-y-auto pr-1 sm:pr-2 -mr-1 sm:-mr-2">
        {/* Image Upload Section */}
        <div className="mb-3 sm:mb-4 space-y-2 flex items-center flex-col">
          <div className="text-[11px] sm:text-[12px] text-gray-300 mb-1 w-full max-w-sm">Upload Background Style Image</div>
          <div className="w-full max-w-sm">
            {!uploadedImage ? (
              <label
                htmlFor="style-image-upload"
                className={`flex flex-col items-center justify-center w-full h-24 sm:h-28 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                  isPreview 
                    ? 'border-white/10 bg-[#0f0f0f]/50' 
                    : 'border-white/20 bg-[#0f0f0f]/30 hover:border-white/30 hover:bg-[#0f0f0f]/40'
                }`}
                onDragOver={(ev) => { ev.preventDefault(); ev.stopPropagation(); }}
                onDrop={(ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  const files = ev.dataTransfer?.files;
                  if (files && files[0]) {
                    handleImageUpload({ target: { files } });
                  }
                }}
              >
                <input
                  id="style-image-upload"
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleImageUpload}
                  disabled={isPreview}
                  className="hidden"
                />
                <FaCloudUploadAlt className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 mb-1" />
                <p className="text-[10px] sm:text-[11px] text-gray-400 text-center px-3">
                  <span className="text-white font-medium">Click to upload</span> or drag and drop
                </p>
                <p className="text-[9px] sm:text-[10px] text-gray-500 mt-0.5">JPG or PNG (max 10MB)</p>
              </label>
            ) : (
              <div className="relative w-full">
                <div className="relative rounded-lg overflow-hidden border border-white/10 bg-[#0f0f0f]">
                  <img
                    src={imagePreview}
                    alt="Uploaded style image"
                    className="w-full h-auto max-h-32 sm:max-h-40 object-contain"
                  />
                  {!isPreview && (
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-1.5 right-1.5 p-1 sm:p-1.5 bg-red-500/90 hover:bg-red-600 rounded-full transition-colors"
                      title="Remove image"
                    >
                      <FaTrash className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                    </button>
                  )}
                </div>
                <p className="text-[9px] sm:text-[10px] text-gray-500 mt-1.5 text-center">
                  {uploadedImage.name}
                </p>
              </div>
            )}
          </div>
        </div>

        {!isPreview && uploadedImage && (
          <div className="mb-3 flex items-center">
            <button type="button" onClick={handleReset} className="text-[11px] text-gray-300 hover:text-white">Reset</button>
            <div className="ml-auto flex items-center gap-2">
              <button type="button" onClick={onClose} className="inline-flex items-center rounded-lg bg-white/10 text-gray-100 px-2.5 py-1 text-[11px] border border-white/10 hover:bg-white/20">Cancel</button>
              <button type="button" onClick={handleApply} className="inline-flex items-center rounded-lg bg-white text-black px-2.5 py-1 text-[11px] hover:opacity-90">Apply ▶</button>
            </div>
          </div>
        )}

        {/* OR Separator */}
        <div className="relative my-3 sm:my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/20"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="bg-[#0a0a0a] px-3 text-[10px] text-gray-400 font-medium">OR</span>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-2 sm:mb-3 space-y-2 sm:space-y-2.5">
          {/* Search Bar */}
          <div className="relative">
            <FaSearch className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs sm:text-sm" />
            <input
              type="text"
              placeholder="Search styles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 sm:pl-11 pr-3 sm:pr-4 py-2 sm:py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-400/50 focus:border-pink-400/50 transition-all"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                <FaTimes className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
            )}
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {categories.map(category => (
              <button
                key={category}
                type="button"
                onClick={() => setSelectedCategory(category)}
                className={`px-2.5 py-1.5 sm:px-3 sm:py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-lg shadow-pink-500/25'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white border border-white/10'
                }`}
              >
                {category}
              </button>
            ))}
            
            {(searchQuery || selectedCategory !== "All") && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="px-2.5 py-1.5 sm:px-3 sm:py-1.5 rounded-lg text-xs font-medium bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10 transition-all duration-200"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Styles Grid */}
        {totalStyles === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <FaSearch className="text-gray-600 text-4xl mb-3" />
            <h4 className="text-lg font-semibold text-gray-400 mb-2">No styles found</h4>
            <p className="text-gray-500 mb-3 text-sm">
              Try adjusting your search or filters
            </p>
            <button
              type="button"
              onClick={handleClearFilters}
              className="px-5 py-2 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 text-white text-sm font-medium hover:shadow-lg hover:shadow-pink-500/25 transition-all duration-200"
            >
              Clear All Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 2xl:grid-cols-12 gap-1.5 sm:gap-2 lg:gap-1.5 pb-2 sm:pb-4">
            {filteredItems.map(item => {
              const styleId = typeof selectedStyle === 'string' ? selectedStyle : (selectedStyle?.styleId || null);
              return (
                <StyleCard
                  key={item.id}
                  item={item}
                  isSelected={styleId === item.id}
                  onClick={() => handleStyleSelect(item.id)}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  if (isPreview) {
    return (
      <div className={`absolute z-50 pointer-events-auto ${className}`}>
        {Panel}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10">
        {Panel}
      </div>
    </div>
  );
};

export default StyleModal;
export { StyleGrid, STYLE_GROUPS }; 